import { BOARD } from '../game/board'
import { QUARANTINE_BAIL, canEndTurn, canRoll } from '../game/engine'
import { awaitingRollChoice, controlsCurrent, useGame } from '../store/gameStore'
import type { GameState } from '../game/types'
import { useEffect, useRef, useState } from 'react'
import { money, short } from '../util/format'
import { SoundToggle } from './SoundToggle'

/** ความคืบหน้าการนับถอยหลังจบตา 0..1 (null = ไม่ได้กำลังนับ) */
function useCountdown(at: number | null): number | null {
  const [k, setK] = useState<number | null>(null)
  const startRef = useRef(0)

  useEffect(() => {
    if (at === null) {
      setK(null)
      return
    }
    startRef.current = Date.now()
    const total = at - startRef.current
    let raf = 0
    const tick = () => {
      const left = at - Date.now()
      setK(Math.min(1, Math.max(0, 1 - left / total)))
      if (left > 0) raf = requestAnimationFrame(tick)
    }
    tick()
    return () => cancelAnimationFrame(raf)
  }, [at])

  return k
}

export function ActionBar({ g }: { g: GameState }) {
  const { roll, useReroll, acceptRoll, finishTurn, bail, toggleRules, quit, holdTurn } = useGame()
  const mine = useGame(controlsCurrent)
  const waitingRoll = useGame(awaitingRollChoice)
  const online = useGame((s) => s.mode === 'online')
  const autoEndAt = useGame((s) => s.autoEndAt)
  const autoEnd = useGame((s) => s.autoEnd)
  const setAutoEnd = useGame((s) => s.setAutoEnd)
  const countdown = useCountdown(autoEndAt)
  const p = g.players[g.current]

  return (
    <div className="actionbar card">
      <div className="ab-turn">
        <span className="ab-ava" style={{ background: p.character.color }}>
          {p.character.emoji}
        </span>
        <span className="ab-who">
          <strong>
            {p.name}
            {p.isBot && <span className="bot-tag">BOT</span>}
          </strong>
          <span className="faint tiny">
            รอบ {g.round} · {BOARD[p.pos].emoji} {BOARD[p.pos].name} · เงินสด {money(p.cash)}
          </span>
        </span>
      </div>

      <div className="ab-buttons">
        {!mine ? (
          <span className="pill waiting">
            {p.isBot ? '🤖 บอทกำลังเล่น…' : `⏳ รอ ${p.name} เล่น…`}
          </span>
        ) : p.quarantine > 0 ? (
          <>
            <span className="pill bad">🧊 ถูกกักกัน เหลือ {p.quarantine} ตา</span>
            <button className="btn btn-warn" disabled={p.cash < QUARANTINE_BAIL} onClick={bail}>
              จ่ายประกันตัว {short(QUARANTINE_BAIL)}
            </button>
            <button className="btn btn-ghost" disabled={!canEndTurn(g)} onClick={finishTurn}>
              รอไปก่อน →
            </button>
          </>
        ) : waitingRoll ? (
          <>
            <button className="btn btn-primary btn-lg" onClick={acceptRoll}>
              ✅ ใช้แต้มนี้
            </button>
            <button className="btn btn-warn" onClick={useReroll}>
              🔄 ทอยใหม่ ({p.rerollLeft})
            </button>
          </>
        ) : (
          <>
            <button className="btn btn-primary btn-lg" disabled={!canRoll(g)} onClick={roll}>
              🎲 ทอยลูกเต๋า
            </button>
            <button
              className={`btn end-btn${countdown !== null ? ' counting' : ''}`}
              disabled={!canEndTurn(g)}
              onClick={finishTurn}
            >
              {countdown !== null && (
                <i className="end-fill" style={{ transform: `scaleX(${1 - countdown})` }} />
              )}
              <span>จบตา →</span>
            </button>
            {countdown !== null && (
              <button className="btn btn-sm btn-ghost" onClick={holdTurn} title="หยุดนับ ขอจัดการพอร์ตก่อน">
                ⏸ ขอคิดก่อน
              </button>
            )}
          </>
        )}
      </div>

      <div className="ab-side">
        <SoundToggle compact />
        <button
          className={`btn btn-sm btn-ghost${autoEnd ? '' : ' muted'}`}
          aria-pressed={autoEnd}
          title={autoEnd ? 'ปิดการจบตาอัตโนมัติ' : 'เปิดการจบตาอัตโนมัติ'}
          onClick={() => setAutoEnd(!autoEnd)}
        >
          ⏭️
        </button>
        <button className="btn btn-sm btn-ghost" onClick={() => toggleRules()}>
          📖 กติกา
        </button>
        <button
          className="btn btn-sm btn-ghost"
          onClick={() => {
            const msg = online
              ? 'ออกจากห้องนี้? เพื่อนที่เหลือจะเล่นต่อได้ และระบบจะเล่นแทนคุณ'
              : 'ออกจากเกมนี้และกลับไปหน้าตั้งค่า? ความคืบหน้าจะถูกลบ'
            if (confirm(msg)) quit()
          }}
        >
          ออก
        </button>
      </div>
    </div>
  )
}
